import GameObject from '../../GameObject/GameObject';
import Armature from '../../Prototype/Armature';
import AnimationClip, {
    AnimationClipEvent,
} from '../../System/Animation/AnimationClip';
import {
    StartAnimationClip,
    StartAnimationClipAsync,
} from '../../System/Animation/AnimationHelper';
import {
    Alpha,
    DragonbonesDefault,
    DragonbonesFadeIn,
} from '../../System/Animation/Clip';
import { PetsHelper } from '../../Utility/Helpers/Pets/PetsHelper';
import { PetArmature } from '../../Utility/Helpers/Pets/PetArmature';
import { PetAnimations } from '../../Utility/Helpers/Pets/PetAnimations';
import TransformComponent from '../../Component/Transform';
import { IPetCreateData } from './IPetCreateData';
import CleanupManager from '../../Utility/CleanupManager';
import { PetId } from '../../Constants/Pets';
import Interactable from '../../Component/Interactable';
import { promiseAllSettled } from '../../Utility/async';
import Renderable from 'Component/Renderable';

export default class Pet extends GameObject {
    public initialized: Promise<void>;
    public readonly armatures: Map<PetArmature, Armature> = new Map<
        PetArmature,
        Armature
    >();

    private readonly onPetIdChangedCM: CleanupManager = new CleanupManager();

    public currentAnimation!: PetAnimations;
    private armatureWhitelist?: PetArmature[];
    private armatureBlacklist?: PetArmature[];
    private dragonbonesName?: string;
    private petId!: PetId;
    private playingId: number = 1;

    public onCreate(params: IPetCreateData): void {
        this.addComponent(TransformComponent, params);
        this.addComponent(Interactable, { buttonMode: true });
        this.dragonbonesName = params.dragonbonesName;
        this.setPetId(params.petId);
        this.armatureBlacklist = params.armatureBlacklist ?? [];
        this.armatureWhitelist =
            params.armatureWhitelist ?? Object.values(PetArmature);
    }

    public setPetId(id: PetId): void {
        if (this.petId !== id) {
            this.petId = id;
            this.onPetIdChangedCM.cleanUpAll();

            if (this.petId) {
                this.initialized = PetsHelper.loadPetAnimations(
                    this.petId, this.dragonbonesName
                ).then(this.initAnimations);
            }
        }
    }

    private readonly initAnimations = (dragonbonesName: string): void => {
        // Create the required armatures
        this.armatureWhitelist
            .filter(armature => !this.armatureBlacklist.includes(armature))
            .forEach((armatureName: PetArmature) => {
                const armature = Pet.createArmature({
                    dragonbonesName,
                    armatureName,
                });
                this.armatures.set(armatureName, armature);
                this.addChild(armature);
                this.onPetIdChangedCM.add(() => {
                    // TODO: Refactor Armature to be updatable with new armature data so we can reuse the armature
                    armature.destroy();
                });
            });
    };

    public getPlayAnimation = (animation: PetAnimations) => {
        return () => this.playAnimation(animation);
    };

    public toWalkingAnimations(): PetAnimations[] {
        const target: PetAnimations = PetAnimations.WALKING;

        if (this.canTransition(this.currentAnimation, target)) return [];

        const currentAnimationData = PetsHelper.getPetAnimationData(
            this.currentAnimation
        );

        const nextPossible = currentAnimationData?.possibleAnimations?.find(
            animation => this.canTransition(animation, target)
        );

        if (nextPossible) {
            return [nextPossible];
        } else {
            // TODO: ? deeper search ?
        }

        return [];
    }

    public get isMoving(): boolean {
        return (this.currentAnimation === PetAnimations.WALKING || this.currentAnimation === PetAnimations.WALKING_TO_IDLE);
    }

    // TODO: Make sure this works with the armature whitelist/blacklist
    public canTransition = (
        from: PetAnimations,
        to: PetAnimations
    ): boolean => {
        const animationData = PetsHelper.getPetAnimationData(from);
        return animationData.possibleAnimations?.includes(to) ?? false;
    };

    private static createArmature = (options: {
        dragonbonesName: string;
        armatureName: PetArmature;
    }): Armature => {
        return GameObject.Instantiate(Armature, {
            ...options,
            name: options.armatureName,
            playOnStart: false,
            playTimes: 1,
            alpha: 0,
        });
    };

    private playAnimation(
        petAnimation: PetAnimations = this.currentAnimation
    ): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (this.destroyed) return;

            const {
                armatureName: currentArmatureName,
            } = PetsHelper.getPetAnimationData(this.currentAnimation);
            const {
                armatureName,
                animationName,
            } = PetsHelper.getPetAnimationData(petAnimation);
            const armature: Armature = this.armatures.get(armatureName);
            const clips: AnimationClip[] = [];

            if (!armature) {
                console.warn(`Armature '${armatureName}' does not exist`);
                reject();
            }

            // If no animation shown
            if (!this.currentAnimation) {
                const dbFadeIn = new DragonbonesFadeIn(armature, {
                    animationName,
                    fadeInTime: 250,
                    concat: true,
                });
                clips.push(dbFadeIn);
                // If not already showing the current armature
            } else if (armatureName !== currentArmatureName) {
                const fadeTime: number = 0;
                const currentArmature: Armature = this.armatures.get(
                    currentArmatureName
                );

                // Fade out current armature
                if (currentArmature) {
                    const fadeOutClip = new Alpha(currentArmature, {
                        startAlpha: 1,
                        endAlpha: 0,
                        duration: fadeTime,
                    });
                    clips.push(fadeOutClip);
                }
                // Fade in new armature
                const dbFadeIn = new DragonbonesFadeIn(armature, {
                    animationName,
                    fadeInTime: fadeTime,
                });
                clips.push(dbFadeIn);
            } else {
                // TODO: Fix animations to not be interrupted or animations system to prevent that
                // Added to be sure that the armature is fully visible before animating the next animation
                armature.getComponent(Renderable).alpha = 1;
                // else add default clip
                const clip = new DragonbonesDefault(armature, {
                    animationName,
                });
                clips.push(clip);
            }

            // Await clips
            this.currentAnimation = petAnimation;

            const settledPromise = promiseAllSettled(
                ...clips.map(clip => StartAnimationClipAsync(clip))
            );
            settledPromise.then(result => resolve());
            settledPromise.catch(error => reject(error));
        });
    }

    public async play(animation: PetAnimations): Promise<void> {
        this.playingId += 1;
        const playingId = this.playingId;

        while (playingId === this.playingId) {
            await this.playAnimation(animation);
        }
    }

    public async playOnce(animation: PetAnimations): Promise<void> {
        this.playingId += 1;
        await this.playAnimation(animation);
    }

    public stop(): void {
        this.playingId += 1;
    }
}
